import React from "react";
import { Link } from "react-router-dom";
import "../css/privacy.scss";
import PolicyPage from "./PolicyPage";

const UPDATED = "October 16, 2026";
const EMAIL = "core@danecay.shop";

const Updated = () => <p className="policy-updated"><em>Last updated: {UPDATED}</em></p>;
const Email = () => <a href={`mailto:${EMAIL}`}>{EMAIL}</a>;

export function PolicyCenter() {
  const groups = [
    {
      title: "Shopping and orders",
      links: [
        ["/order-policy", "Order & Cancellation Policy"],
        ["/payment-policy", "Payment & Billing Policy"],
        ["/pricing-promotions", "Pricing & Promotions Policy"],
        ["/shipping", "Shipping Policy"],
        ["/duties-taxes", "Customs, Duties & Taxes Policy"],
        ["/returns", "Returns & Refunds Policy"],
        ["/warranty", "Quality & Defects Policy"],
      ],
    },
    {
      title: "Products and customers",
      links: [
        ["/product-care", "Product, Sizing & Care Policy"],
        ["/product-safety", "Product Safety & Recall Policy"],
        ["/user-content", "Reviews & User Content Policy"],
        ["/accessibility", "Accessibility Statement"],
        ["/complaints", "Complaints & Resolution Policy"],
      ],
    },
    {
      title: "Privacy, security and legal",
      links: [
        ["/privacy", "Privacy Policy"],
        ["/privacy-choices", "Privacy Choices / Do Not Sell or Share"],
        ["/cookies", "Cookie & Local Storage Policy"],
        ["/marketing-communications", "Marketing Communications Policy"],
        ["/security", "Security & Fraud Prevention Policy"],
        ["/intellectual-property", "Intellectual Property & Infringement Policy"],
        ["/terms", "Terms of Use and Sale"],
      ],
    },
  ];

  return (
    <PolicyPage title="Policy Center">
      <Updated />
      <p>This center brings together the policies that apply when you browse or shop with danecay. They are written for this store and should be read together. Nothing in them limits consumer rights that cannot lawfully be limited.</p>
      <div className="policy-directory">
        {groups.map((group) => (
          <section className="policy-directory__group" key={group.title}>
            <h2>{group.title}</h2>
            <ul>
              {group.links.map(([to, label]) => (
                <li key={to}><Link to={to}>{label}</Link></li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <h2>Questions</h2>
      <p>If a policy does not answer your question, email <Email /> before ordering. Please include the product or order number when relevant.</p>
    </PolicyPage>
  );
}

export function PaymentPolicy() {
  return (
    <PolicyPage title="Payment & Billing Policy">
      <Updated />
      <h2>Currency and Total</h2>
      <p>Store prices and checkout totals are displayed in USD. Your total consists of the product subtotal, the USD 9.99 standard shipping charge, and any other amount clearly shown before payment. Import charges that a destination authority or carrier collects separately are not part of the store total.</p>

      <h2>Payment Method</h2>
      <p>Available payment methods are shown at checkout. After you select Pay Now, you are redirected to the payment provider. Card numbers and card security codes are entered with that provider and are not entered or stored on this website.</p>

      <h2>Authorisation and Order Acceptance</h2>
      <p>A payment authorisation does not by itself mean that an order has been accepted. Acceptance occurs when payment is confirmed and we issue an order confirmation. A bank may temporarily show a pending authorisation even if a payment fails or an order is cancelled; release timing is controlled by the bank.</p>

      <h2>Billing Accuracy and Fraud Checks</h2>
      <p>You must provide accurate contact and billing information and be authorised to use the selected payment method. We or the payment provider may verify a transaction, request reasonable confirmation, or decline it when fraud, sanctions, chargeback or security indicators are present.</p>

      <h2>Failed, Duplicate or Unrecognised Charges</h2>
      <p>If checkout reports a failure, check with your bank before trying again. For a possible duplicate or unrecognised charge, email <Email /> with the date, amount and order email. Do not send a full card number or security code.</p>

      <h2>Refunds and Exchange Rates</h2>
      <p>Approved refunds are issued to the original payment method. Your bank or card network controls posting time and any currency conversion. Differences caused by exchange rates or bank fees are outside danecay's control unless applicable law requires otherwise.</p>
    </PolicyPage>
  );
}

export function OrderPolicy() {
  return (
    <PolicyPage title="Order & Cancellation Policy">
      <Updated />
      <h2>Placing an Order</h2>
      <p>Review the item, size, colour, quantity, delivery address and email before payment. Submitting checkout is an offer to purchase. An automated receipt only confirms that the request was received; the order is accepted after payment verification and confirmation by danecay.</p>

      <h2>Availability and Verification</h2>
      <p>Stock can change before an order is accepted. We may contact you to verify information or cancel an affected order because of unavailable stock, an obvious price or description error, payment failure, suspected fraud, a restricted destination, or inability to fulfil the order lawfully. Any captured amount for a cancelled item will be refunded.</p>

      <h2>Changes and Cancellation</h2>
      <p>Email <Email /> as soon as possible to request a size, colour, address or cancellation change. We will try to help, but a change is not confirmed until we reply. Once an order has entered packing or has shipped, it normally cannot be changed or cancelled; the <Link to="/returns">Returns & Refunds Policy</Link> then applies.</p>

      <h2>Order Confirmation and Records</h2>
      <p>Use an email address you can access and keep the confirmation for your records. Contact us if no confirmation arrives after a successful payment. Order communications may include payment status, fulfilment, tracking, delay, cancellation, return and safety notices.</p>

      <h2>Personal-Use Orders</h2>
      <p>The store is intended for genuine retail purchases. We may limit quantities or decline orders that appear to involve unauthorised resale, automated purchasing, abusive returns or evasion of transaction limits.</p>

      <h2>No Pre-orders or Subscriptions Unless Stated</h2>
      <p>An item is not a pre-order, recurring purchase or subscription unless its product page and checkout clearly say so. The current store does not enrol customers in recurring product payments.</p>
    </PolicyPage>
  );
}

export function WarrantyPolicy() {
  return (
    <PolicyPage title="Quality & Defects Policy">
      <Updated />
      <h2>Your Legal Rights</h2>
      <p>Products should match their description and be of acceptable quality as required by applicable consumer law. This policy adds a practical reporting process and does not replace or reduce any mandatory warranty, conformity or remedy rights.</p>

      <h2>Reporting a Possible Defect</h2>
      <p>Email <Email /> promptly with the order number, a description of the problem, when it appeared, and clear photos of the product, sole and affected area. Keep the item and packaging until we finish the review.</p>

      <h2>Assessment and Remedy</h2>
      <p>We may ask reasonable questions or request return of the item for inspection. If a manufacturing or fulfilment defect is confirmed, we will provide the remedy required by applicable law, which may be repair, replacement, price reduction or refund. We cover reasonable return costs when the return is required because of our confirmed error or a qualifying defect.</p>

      <h2>What Is Not a Manufacturing Defect</h2>
      <p>Normal creasing, grain or colour variation in natural leather, gradual outsole wear, fit preference, accidental damage, misuse, unauthorised alteration, improper cleaning, prolonged moisture, heat exposure and ordinary wear are not manufacturing defects. These exclusions do not apply where the condition was caused by a defect present at delivery.</p>

      <h2>No Separate Commercial Guarantee</h2>
      <p>Unless a product page or written confirmation expressly provides one, danecay does not offer a separate time-limited commercial guarantee beyond rights that apply under law and the remedies stated in this policy.</p>
    </PolicyPage>
  );
}

export function ProductCarePolicy() {
  return (
    <PolicyPage title="Product, Sizing & Care Policy">
      <Updated />
      <h2>Product Information</h2>
      <p>We aim to describe materials, colours, dimensions and construction accurately. Screens display colour differently, and genuine leather can show natural grain, tone, texture and creasing variations. These variations make each pair slightly different and are not automatically defects.</p>

      <h2>Sizing and Fit</h2>
      <p>Use the size selector and fit notes on the relevant product page. Foot length, width, instep and sock thickness can affect fit, and the same labelled size may feel different between lasts. Sizing guidance is an estimate rather than a guarantee. Contact <Email /> before ordering if you are between sizes or need help.</p>

      <h2>Trying on Shoes</h2>
      <p>Try footwear indoors on a clean, soft surface with the type of socks you plan to wear. Do not remove tags or wear the pair outdoors until you are satisfied with the fit. Return eligibility is governed by the <Link to="/returns">Returns & Refunds Policy</Link>.</p>

      <h2>Leather Care</h2>
      <ul>
        <li>Wipe surface dust with a soft, dry cloth after wear.</li>
        <li>Use only a suitable leather-care product and test it on a hidden area first.</li>
        <li>Do not machine wash, soak, bleach, tumble dry or place near direct heat.</li>
        <li>Allow damp footwear to dry naturally in a ventilated place.</li>
        <li>Store away from prolonged sunlight and use light stuffing to help retain shape.</li>
      </ul>

      <h2>Wear and Safety</h2>
      <p>Outsoles, heel tips and finishes wear with use. Stop wearing a product if a loose component, damaged sole or loss of traction makes it unsafe, and see our <Link to="/product-safety">Product Safety & Recall Policy</Link>.</p>
    </PolicyPage>
  );
}

export function DutiesPolicy() {
  return (
    <PolicyPage title="Customs, Duties & Taxes Policy">
      <Updated />
      <h2>International Orders</h2>
      <p>Orders crossing a border may be reviewed by customs. The destination country determines whether import duty, VAT, sales tax, brokerage or a carrier disbursement fee applies.</p>

      <h2>Amounts Collected by Third Parties</h2>
      <p>Unless checkout expressly states that a particular import charge is included, duties and import taxes are not collected by danecay and may be charged to the recipient by an authority or carrier. These amounts are not additional shipping charges paid to danecay.</p>

      <h2>Customer Responsibility</h2>
      <p>The recipient is responsible for accurate customs information requested at checkout or by the carrier and for charges lawfully imposed at destination. Contact your local customs authority for an estimate before ordering; danecay cannot provide tax advice or guarantee an assessment.</p>

      <h2>Refusal or Non-payment</h2>
      <p>A parcel refused or unclaimed because of import charges may be returned, held or destroyed under carrier rules. Where a refund is legally available, unrecoverable duties, return transport or carrier costs may be deducted to the extent permitted by law. Contact <Email /> before refusing a delivery so we can review the options.</p>

      <h2>Customs Delays</h2>
      <p>Customs clearance can extend delivery estimates. We will assist with reasonable order documents but cannot direct a government authority or guarantee release timing.</p>
    </PolicyPage>
  );
}

export function PricingPolicy() {
  return (
    <PolicyPage title="Pricing & Promotions Policy">
      <Updated />
      <h2>Displayed Prices</h2>
      <p>Prices are displayed in USD and may change for future orders. The price shown in the cart before checkout is the current product price; shipping and any other store-collected charge are itemised separately.</p>

      <h2>Obvious Errors</h2>
      <p>We try to keep pricing accurate. If a price is clearly incorrect because of a technical or typographical error, we may correct it before accepting the order. If payment was captured, we will contact you with the correct option or cancel and refund the affected item.</p>

      <h2>Promotions</h2>
      <p>A promotion applies only for its stated period, products, territory and eligibility rules. Unless the offer says otherwise, discounts cannot be combined, have no cash value, do not apply to previous purchases, and may be limited to one use per customer or order.</p>

      <h2>Returns from Promotional Orders</h2>
      <p>When part of a discounted order is returned, the refund reflects the amount actually paid for the returned item and may account for any minimum-spend condition that is no longer met, as permitted by law. The refund will never exceed the amount paid for that item.</p>

      <h2>Third-party Listings</h2>
      <p>Only prices and offers published on this store or confirmed by danecay are valid here. We are not responsible for expired or inaccurate coupon information posted by unauthorised third parties.</p>
    </PolicyPage>
  );
}

export function AccessibilityStatement() {
  return (
    <PolicyPage title="Accessibility Statement">
      <Updated />
      <h2>Our Commitment</h2>
      <p>danecay aims to make its online store usable by people with different abilities, devices and assistive technologies. Accessibility is an ongoing practice, not a one-time claim of perfect conformance.</p>

      <h2>Design Approach</h2>
      <p>We work toward clear headings, keyboard-operable controls, text alternatives for meaningful images, readable contrast, labelled form fields, responsive layouts and understandable error messages. We use the Web Content Accessibility Guidelines as a reference when reviewing improvements.</p>

      <h2>Known Limitations</h2>
      <p>Some third-party payment or service interfaces are operated outside this website and may behave differently. Natural product photography can also contain fine visual detail that is difficult to describe completely in alternative text.</p>

      <h2>Assistance and Feedback</h2>
      <p>If you cannot access product information or complete an order, email <Email /> with the page, device or assistive technology used and the task you were trying to complete. We will offer the information in a reasonable alternative format or help through an accessible channel where possible.</p>

      <h2>Response</h2>
      <p>We review accessibility feedback alongside other customer-care requests and prioritise barriers that prevent browsing, understanding a product, or completing checkout.</p>
    </PolicyPage>
  );
}

export function IntellectualPropertyPolicy() {
  return (
    <PolicyPage title="Intellectual Property & Infringement Policy">
      <Updated />
      <h2>Respect for Rights</h2>
      <p>danecay respects copyright, trademark, design and other intellectual-property rights. Store content should be original, licensed, in the public domain, or otherwise used with a lawful basis. A third-party name or mark is used only when reasonably necessary to identify a compatible service, payment method or factual source and does not imply endorsement.</p>

      <h2>danecay Materials</h2>
      <p>The danecay name, logos, original copy, product presentation and store design are protected to the extent provided by law. Personal browsing and shopping are permitted. Republishing, selling, impersonating, removing rights notices from, or using store materials to create consumer confusion requires prior written permission.</p>

      <h2>User Responsibility</h2>
      <p>Do not send or submit images, text, reviews or other materials unless you created them or have permission to use them. Do not submit counterfeit product claims, copied listings, confidential material or content that misuses another person's name, image or rights.</p>

      <h2>How to Report Possible Infringement</h2>
      <p>Email <Email /> with the subject “Rights Notice” and include: your contact details; the protected work, mark or design; the exact page or material at issue; why you believe the use is unauthorised; your relationship to the rights holder; and a statement that the information is accurate and submitted in good faith. Links and supporting documents help us locate and assess the material.</p>

      <h2>Review Process</h2>
      <p>We may ask for authority or ownership information, notify the person who supplied the material, and remove or restrict content while a claim is reviewed. Affected parties may send a reasoned response with evidence of permission or another lawful basis. We will take appropriate action under applicable law; submitting a knowingly false notice may create liability.</p>

      <h2>No Affiliation with Reference Stores</h2>
      <p>danecay is an independent brand. Unless expressly stated, it is not affiliated with or endorsed by another footwear store or brand. Similar product categories, functional footwear terms and common ecommerce features do not indicate a commercial connection.</p>
    </PolicyPage>
  );
}

export function UserContentPolicy() {
  return (
    <PolicyPage title="Reviews & User Content Policy">
      <Updated />
      <h2>Current Store Features</h2>
      <p>The current store does not provide a public account, comment or review-submission tool. If a public review feature is introduced, this policy will apply together with any instructions shown at submission.</p>

      <h2>Honest and Relevant Content</h2>
      <p>Reviews and feedback must reflect a genuine experience and should be relevant, respectful and factually supportable. Undisclosed paid reviews, impersonation, spam, threats, personal data, illegal content and discriminatory or obscene material are not permitted.</p>

      <h2>Rights to Submitted Material</h2>
      <p>You retain ownership of content you create. Sending customer-service material does not give danecay permission to publish it as advertising. We will request separate permission before publicly using a review, photo or testimonial received by email, unless disclosure is required to resolve a dispute or comply with law.</p>

      <h2>Permission and Privacy</h2>
      <p>Only submit material you own or are authorised to share. Obtain consent from identifiable people and remove addresses, order numbers, payment details and other unnecessary personal information from public-facing content.</p>

      <h2>Moderation</h2>
      <p>If public submissions become available, we may reject or remove content that violates this policy, while not suppressing a review merely because it is critical. Suspected infringement can be reported under the <Link to="/intellectual-property">Intellectual Property & Infringement Policy</Link>.</p>
    </PolicyPage>
  );
}

export function MarketingPolicy() {
  return (
    <PolicyPage title="Marketing Communications Policy">
      <Updated />
      <h2>Transactional Messages</h2>
      <p>Order confirmations, payment status, delivery updates, return messages, service replies and product-safety notices are transactional. They may be sent when needed to perform a purchase or provide requested support and are not promotional subscriptions.</p>

      <h2>Email Marketing</h2>
      <p>We do not use checkout contact details for unsolicited marketing. If danecay introduces an optional newsletter, enrolment will use a clear choice and each marketing email will provide an unsubscribe method. Unsubscribing from promotions does not stop necessary order or safety messages.</p>

      <h2>SMS and Automated Calls</h2>
      <p>danecay does not currently operate a promotional SMS or automated-call programme. Placing an order does not consent you to marketing texts or calls. If such a programme is introduced, its frequency, consent terms, charges and opt-out instructions will be presented before enrolment.</p>

      <h2>Preference Requests</h2>
      <p>To ask about a message or request suppression of promotional email, contact <Email /> and include the receiving address. We may retain a minimal suppression record so that the preference continues to be honoured.</p>
    </PolicyPage>
  );
}

export function SecurityPolicy() {
  return (
    <PolicyPage title="Security & Fraud Prevention Policy">
      <Updated />
      <h2>Store Safeguards</h2>
      <p>We use reasonable administrative and technical measures designed to protect store and order information. Payment credentials are entered with the payment provider rather than on this website. No online service can promise absolute security.</p>

      <h2>Transaction Screening</h2>
      <p>Orders may be screened for unusual device, payment, location, sanctions, chargeback or delivery patterns. We may request limited verification, delay fulfilment during review, or cancel and refund a transaction when reasonably necessary to protect customers and the store.</p>

      <h2>Protect Yourself</h2>
      <ul>
        <li>Use only this store's displayed checkout flow and check the domain before paying.</li>
        <li>danecay will not ask by email for a full card number, card security code or account password.</li>
        <li>Do not send payment credentials to <Email />.</li>
        <li>Report a suspicious message or unauthorised order promptly and contact your payment provider where appropriate.</li>
      </ul>

      <h2>Responsible Security Reports</h2>
      <p>To report a suspected vulnerability, email <Email /> with the subject “Security Report,” the affected page, steps to reproduce and potential impact. Do not access another person's data, disrupt service, use destructive testing, or publicly disclose the issue before we have had reasonable time to investigate. This is not a bug-bounty programme and does not promise payment.</p>

      <h2>Incident Response</h2>
      <p>We investigate credible security reports, take proportionate containment and remediation steps, and provide notices to affected people or authorities when required by applicable law.</p>
    </PolicyPage>
  );
}

export function ComplaintsPolicy() {
  return (
    <PolicyPage title="Complaints & Resolution Policy">
      <Updated />
      <h2>How to Make a Complaint</h2>
      <p>Email <Email /> with the subject “Complaint” and include your order number, the issue, relevant dates, the resolution you are seeking, and supporting photos or messages. Do not include full payment-card details.</p>

      <h2>Our Review</h2>
      <p>We normally acknowledge messages within two business days. We aim to provide a substantive response within 14 calendar days, although carrier, payment or technical investigations may take longer. If more time is needed, we will explain the status and next step.</p>

      <h2>Fair Resolution</h2>
      <p>We review the order record, applicable policy and evidence from both sides. Depending on the issue and law, an appropriate outcome may include an explanation, correction, replacement, repair, refund or another practical remedy.</p>

      <h2>Escalation</h2>
      <p>If you disagree with the first response, reply with “Escalation Request” and explain what was missed. A person not responsible for the initial answer will review it where reasonably possible. You may also use any consumer-protection, payment-dispute or court process available under applicable law; this policy does not restrict those rights.</p>

      <h2>Abusive Communications</h2>
      <p>We will address firm criticism and genuine disputes. Threats, harassment, discriminatory abuse, spam or attempts to obtain an unwarranted benefit may be limited or reported where necessary, without preventing legitimate legal claims.</p>
    </PolicyPage>
  );
}

export function ProductSafetyPolicy() {
  return (
    <PolicyPage title="Product Safety & Recall Policy">
      <Updated />
      <h2>Safe Use</h2>
      <p>Footwear should be used for its ordinary intended purpose and in conditions suited to its construction. Check the sole, heel, fastenings and upper before wear. New outsoles can be slippery on some surfaces; take particular care on wet, oily, icy or polished floors.</p>

      <h2>Materials and Sensitivities</h2>
      <p>Product pages identify key materials where available. Stop use if a material appears to cause irritation and seek appropriate medical advice. For a known allergy or sensitivity, contact <Email /> before ordering; information we provide is general product information, not medical advice.</p>

      <h2>Children and Small Parts</h2>
      <p>Unless a product page says otherwise, danecay footwear is designed for adults. Packaging, tags, buckles and detachable pieces should be kept away from young children and pets.</p>

      <h2>Report a Safety Incident</h2>
      <p>Stop using the item and email <Email /> with the order number, product, size, photos, a description of what occurred and whether anyone was injured. Preserve the product and packaging because they may be needed for assessment. Seek urgent local assistance first if immediate health or safety is at risk.</p>

      <h2>Corrective Action and Recalls</h2>
      <p>If a product presents a confirmed safety issue, we may contact affected customers using order details and publish instructions on this store. Follow any stop-use, return, repair, replacement or disposal direction in the notice. Safety remedies will be provided as required by applicable law.</p>
    </PolicyPage>
  );
}

export function PrivacyChoices() {
  return (
    <PolicyPage title="Privacy Choices / Do Not Sell or Share">
      <Updated />
      <h2>Current Practice</h2>
      <p>danecay does not sell personal information for money. The current storefront does not use advertising pixels or disclose personal information for cross-context behavioural advertising. For that reason, there is currently no separate advertising opt-out toggle on this site.</p>

      <h2>Browser Storage</h2>
      <p>The store uses browser local storage to remember cart contents on your device. You can clear it through browser settings, although doing so removes the saved cart. The payment provider may use its own necessary technologies after you leave this store for payment.</p>

      <h2>Your Requests</h2>
      <p>Depending on where you live, you may have rights to know or access personal information, correct it, delete it, receive a portable copy, object to or restrict certain processing, withdraw consent, or appeal a privacy decision. Legal exceptions may apply, including order, fraud-prevention, tax and recordkeeping duties.</p>

      <h2>How to Exercise a Choice</h2>
      <p>Email <Email /> with the subject “Privacy Request,” the request type, your name, and the order email if relevant. Do not send identity documents unless we request a proportionate verification method. An authorised agent should provide evidence of authority where required.</p>

      <h2>Verification, Timing and Non-discrimination</h2>
      <p>We may verify that a request concerns your information before acting. We will respond within the period required by applicable law and explain any lawful denial or appeal route. We will not deny ordinary service or charge a different price solely because you exercised a privacy right, except where a lawful difference is directly related to the value of data involved.</p>

      <h2>Future Changes</h2>
      <p>If our advertising or data-disclosure practices change, we will update this page and provide any legally required preference control before the new practice begins.</p>
    </PolicyPage>
  );
}

